import React from 'react';
import {
  AlertTriangleIcon,
  ClockIcon,
  FolderIcon,
  TimerIcon,
  PaperclipIcon,
  UserIcon,
} from 'lucide-react';
import { Badge } from '@components/atoms/Badge';
import { useTranslation } from '@hooks/useTranslation';

interface DetailedIncidentCardProps {
  name: string;
  status: string;
  additionalInfo: string;
  date: string;
  type: string;
  delay: string;
  projectName?: string;
  reportedBy?: string;
  documentsCount?: number;
  onClick?: () => void;
}

export function DetailedIncidentCard({
  name,
  status,
  additionalInfo,
  date,
  type,
  delay,
  projectName,
  reportedBy,
  documentsCount,
  onClick,
}: DetailedIncidentCardProps) {
  const tIncidents = useTranslation('incidents');
  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 p-4 shadow-sm ${
        onClick
          ? 'cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200'
          : ''
      }`}
      onClick={onClick}>
      <div className="space-y-3">
        {/* Header row */}
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1 min-w-0">
            <AlertTriangleIcon
              size={20}
              className="text-gray-400 mr-2 mt-0.5 flex-shrink-0"
            />
            <h2 className="text-lg font-medium text-gray-900 truncate">
              {name}
            </h2>
          </div>
          <div className="flex gap-2 ml-3 flex-shrink-0">
            <Badge variant="success" size="sm">
              {status}
            </Badge>
            <Badge variant="secondary" size="sm">
              {type}
            </Badge>
          </div>
        </div>

        {/* Description */}
        {additionalInfo && additionalInfo !== 'No additional information' && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {additionalInfo}
          </p>
        )}

        {/* Info row */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <ClockIcon size={14} />
            <span>{date}</span>
          </div>
          {projectName && (
            <div className="flex items-center gap-1.5">
              <FolderIcon size={14} />
              <span>{projectName}</span>
            </div>
          )}
          {delay && delay !== 'N/A' && (
            <div className="flex items-center gap-1.5">
              <TimerIcon size={14} />
              <span>{delay}</span>
            </div>
          )}
          {reportedBy && (
            <div className="flex items-center gap-1.5">
              <UserIcon size={14} />
              <span>{reportedBy}</span>
            </div>
          )}
          {documentsCount !== undefined && documentsCount > 0 && (
            <div className="flex items-center gap-1.5">
              <PaperclipIcon size={14} />
              <span>
                {(documentsCount === 1 ? tIncidents.documentCountOne : tIncidents.documentCountOther).replace(
                  '{n}',
                  String(documentsCount),
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
